import { useTranslation } from 'react-i18next'
import { Params, useLocation, useParams, useRouteError } from 'react-router-dom'

import logger from '@cash-web/shared/util-logger'
import { useExperiment } from '@cash-web/shared-feature-experiments'
import { RedirectToMobile } from '@cash-web/shared-feature-redirect-to-mobile'
import { createErrorBlocker, FlowExecuteOnce } from '@cash-web/shared-flow-feature-main'
import { useExecuteOnce } from '@cash-web/shared-util-execute-once'
import { HttpError } from '@cash-web/shared-util-fetch'

const makeRedirectToMobileUrls = (pathname: string, params: Readonly<Params<string>>, search: string) => {
  let clientRouteUrl = `https://internal.cash.app${pathname}${search}`
  let deepLinkPath: `/${string}` | undefined = undefined

  if (pathname.startsWith('/web/flow/') && params['initiationData']) {
    clientRouteUrl = `https://internal.cash.app/dl/flow/${encodeURIComponent(params['initiationData'])}${search}`
    deepLinkPath = `/f/${encodeURIComponent(params['initiationData'])}${search}`
  }
  if (pathname.startsWith('/web/flow/') && params['flowType']) {
    clientRouteUrl = `https://internal.cash.app/dl/flow/type/${params['flowType']}${search}`
    deepLinkPath = `/f/${params['flowType']}${search}`
  }
  if (pathname.startsWith('/dl/flow/')) {
    const flowType = params['flowType'] ?? params['initiationData']
    if (flowType) {
      deepLinkPath = `/f/${encodeURIComponent(flowType)}${search}`
    }
  }

  return { clientRouteUrl, deepLinkPath }
}

export const JourneyDefaultError = () => {
  const { t } = useTranslation()
  const isPushNotificationEnabled =
    useExperiment('cash-web-continue-in-mobile-push-notification')?.variant_value === 'treatment'
  const error = useRouteError()
  const params = useParams()
  const location = useLocation()

  useExecuteOnce(() => {
    if (error instanceof Error) {
      logger.info(new Error(`JourneyDefaultError: ${error.message}`))
    }
  })

  const isFlowRoute = location.pathname.startsWith('/dl/flow/') || location.pathname.startsWith('/web/flow/')
  const isWebOnlyClientRoute = location.pathname.startsWith('/web/') && !isFlowRoute

  const isAuthError = error instanceof HttpError && error.response?.status === 401

  /*
   * Push notification support is currently blocked by https://linear.app/squareup/issue/CWP-1659/clientroute-url-is-not-launching-from-prod-push-notifications
   * So we can only handle flow routes via deep links (/f/{flowType}). Once resolved (upon release of 5.38), we can begin to roll out the 'cash-web-continue-in-mobile-push-notification'
   * experiment to handle resuming all client routes on mobile via push notifications.
   */
  const isPushNotificationEligible = (isPushNotificationEnabled && !isWebOnlyClientRoute) || isFlowRoute

  // Because auth errors don't imply that the flow cannot be handled by web, we should not redirect to mobile.
  if (isPushNotificationEligible && !isAuthError) {
    const { clientRouteUrl, deepLinkPath } = makeRedirectToMobileUrls(location.pathname, params, location.search)
    return <RedirectToMobile clientRouteUrl={clientRouteUrl} deepLinkPath={deepLinkPath} />
  }

  return (
    <FlowExecuteOnce
      onExecute={flow =>
        flow.startWithBlockerDescriptors({
          trackingFlowName: 'Default Error',
          blockerDescriptors: [{ blocker: { form: createErrorBlocker(t) } }],
          queriesToInvalidate: [],
        })
      }
    />
  )
}
